import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  useCarePathwayPresets,
  useResolveCarePathway,
} from '../../state/data/pathway';
import { readAccessSubtrack } from '../../logic/onboardingReview';
import {
  diagnosisDeltaForSubtrack, hasDiagnosisChange, panelConditionRows,
} from '../../logic/pathwayConditions';

/**
 * SubtrackPicker — resolved-subtrack banner + single-select pathway picker + live
 * WF36 resolve preview. Reports the selection up via `onSelectionChange` so the host
 * drives the apply. Shared by PathwayPresetModal + OnboardingReviewModal.
 * Invariant: `selectedId` seeds once from carePlan at mount — host must mount this
 * only after carePlan is loaded and key it by patient so it remounts on a switch.
 */
export default function SubtrackPicker({ patient, carePlan, onSelectionChange }) {
  const presetsQuery = useCarePathwayPresets();
  const presets = presetsQuery.data ?? null;
  const loadError = presetsQuery.error?.message ?? null;
  const resolveMut = useResolveCarePathway();
  // mutateAsync is effectively stable for this bare mutation; do NOT read
  // resolveMut.isPending/data anywhere or this effect's deps will thrash.
  const resolvePathway = resolveMut.mutateAsync;

  const subtrack = useMemo(() => readAccessSubtrack(carePlan), [carePlan]);
  const [selectedId, setSelectedId] = useState(() => subtrack?.subtrack_id ?? null);
  const [ckdStage, setCkdStage] = useState(() => initialCkdStage(carePlan));
  const [preview, setPreview] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const resolveTimer = useRef(null);

  const pathwayRows = useMemo(
    () => (presets || []).filter(p => p.kind === 'pathway'),
    [presets],
  );
  // Pin the suggested subtrack to the top (avoid scrolling to find it); drop it
  // from its track group so it isn't listed twice.
  const suggestedRow = useMemo(
    () => pathwayRows.find(p => p.preset_id === subtrack?.subtrack_id) || null,
    [pathwayRows, subtrack],
  );
  const groups = useMemo(() => {
    const rest = pathwayRows.filter(p => p.preset_id !== suggestedRow?.preset_id);
    return {
      eCKM: rest.filter(p => p.track_preference === 'eCKM'),
      CKM: rest.filter(p => p.track_preference === 'CKM'),
    };
  }, [pathwayRows, suggestedRow]);

  const selectedRow = useMemo(
    () => pathwayRows.find(p => p.preset_id === selectedId) || null,
    [pathwayRows, selectedId],
  );
  const showStage = Array.isArray(selectedRow?.stage_options) && selectedRow.stage_options.length > 0;
  const isOverride = subtrack?.subtrack_id && selectedId && selectedId !== subtrack.subtrack_id;
  // What the recorded diagnosis becomes if this subtrack is applied — Apply attests it.
  const diagnosisDelta = useMemo(
    () => diagnosisDeltaForSubtrack(carePlan?.conditions, selectedId),
    [carePlan, selectedId],
  );
  // One chip per condition with the billed ICD-10 inline — same math as $align.
  const panelRows = useMemo(
    () => panelConditionRows(carePlan?.conditions, subtrack?.subtrack_id ?? null, diagnosisDelta, selectedId),
    [carePlan, subtrack, diagnosisDelta, selectedId],
  );

  // Debounced resolve preview on selection / stage change.
  useEffect(() => {
    if (!presets) return undefined;
    if (!selectedId) { setPreview(null); return undefined; }
    let cancelled = false;   // discard a stale resolve if the selection changes / unmounts mid-flight
    if (resolveTimer.current) clearTimeout(resolveTimer.current);
    resolveTimer.current = setTimeout(async () => {
      setResolving(true);
      setResolveError(null);
      try {
        const result = await resolvePathway({
          withingsUserId: patient.userid,
          selected: [selectedId],
          ckdStage: showStage ? ckdStage : null,
        });
        if (!cancelled) setPreview(result);
      } catch (err) {
        // Surface the error (don't swallow) so the host can still let the user
        // finish — apply re-resolves server-side anyway.
        if (!cancelled) { setPreview(null); setResolveError(err?.message || 'Could not preview the plan.'); }
      } finally {
        if (!cancelled) setResolving(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      if (resolveTimer.current) clearTimeout(resolveTimer.current);
    };
  }, [selectedId, ckdStage, showStage, presets, patient.userid, resolvePathway]);

  // Report the current selection upward so the host modal can drive apply.
  useEffect(() => {
    onSelectionChange?.({
      selectedId,
      ckdStage: showStage ? ckdStage : null,
      showStage,
      preview,
      resolving,
      loadError,
      resolveError,
      isOverride,
      suggestedId: subtrack?.subtrack_id ?? null,
      diagnosisDelta,
    });
  }, [selectedId, ckdStage, showStage, preview, resolving, loadError, resolveError, isOverride, subtrack, diagnosisDelta, onSelectionChange]);

  return (
    <>
      {(loadError || resolveError) && (
        <div style={{
          background: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)',
          padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
        }}>{loadError || `${resolveError} You can still confirm — the plan is re-resolved on save.`}</div>
      )}

      {!presets && !loadError && (
        <div style={{ color: 'var(--wi-text-secondary)', fontSize: 13, padding: 12 }}>
          Loading pathways…
        </div>
      )}

      {presets && (
        <>
          <SubtrackBanner subtrack={subtrack} selectedRow={selectedRow} isOverride={isOverride} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: 20, alignItems: 'start', marginTop: 14 }}>
            {/* ── Left column — subtrack picker (single-select) ── */}
            <div>
              <div style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: 'var(--wi-text-secondary)',
                marginBottom: 10,
              }}>
                Subtrack pathway ({pathwayRows.length})
              </div>
              {suggestedRow && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--wi-status-info)', letterSpacing: '0.04em', marginBottom: 6 }}>
                    SUGGESTED
                  </div>
                  <SubtrackOption
                    preset={suggestedRow}
                    checked={selectedId === suggestedRow.preset_id}
                    suggested
                    onSelect={() => setSelectedId(suggestedRow.preset_id)}
                    extra={selectedId === suggestedRow.preset_id && Array.isArray(suggestedRow.stage_options) && suggestedRow.stage_options.length
                      ? <CkdStageSegmented stage={ckdStage} onChange={setCkdStage} options={suggestedRow.stage_options} />
                      : null}
                  />
                </div>
              )}
              {['eCKM', 'CKM'].map(track => groups[track].length > 0 && (
                <div key={track} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--wi-text-placeholder)', letterSpacing: '0.04em', marginBottom: 6 }}>
                    {track}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {groups[track].map(p => (
                      <SubtrackOption
                        key={p.preset_id}
                        preset={p}
                        checked={selectedId === p.preset_id}
                        suggested={subtrack?.subtrack_id === p.preset_id}
                        onSelect={() => setSelectedId(p.preset_id)}
                        extra={selectedId === p.preset_id && Array.isArray(p.stage_options) && p.stage_options.length
                          ? <CkdStageSegmented stage={ckdStage} onChange={setCkdStage} options={p.stage_options} />
                          : null}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Right column — Resolved plan preview ── */}
            <div>
              <div style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: 'var(--wi-text-secondary)',
                marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                Resolved plan
                {resolving && (
                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--wi-text-placeholder)', textTransform: 'none', letterSpacing: 0 }}>
                    updating…
                  </span>
                )}
              </div>
              {selectedId && diagnosisDelta && <DiagnosisPanel delta={diagnosisDelta} rows={panelRows} />}
              {!preview ? <EmptyPreview /> : <PreviewPanel preview={preview} />}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function initialCkdStage(carePlan) {
  const applied = (carePlan && carePlan.applied_pathways) || [];
  const ckdTag = applied.find(id => typeof id === 'string' && /-3[ab]$/.test(id));
  if (ckdTag) return ckdTag.slice(-2);
  return '3a';
}

/* ─── Resolved-subtrack banner ─── */
function SubtrackBanner({ subtrack, selectedRow, isOverride }) {
  if (!subtrack || !subtrack.subtrack_id) {
    return (
      <div style={{
        background: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)',
        borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.5,
      }}>
        No subtrack resolved for this patient yet — pick the matching pathway below.
      </div>
    );
  }
  return (
    <div style={{
      background: 'var(--wi-background-info)', color: 'var(--wi-text-primary-info)',
      borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 13 }}>
        <span style={{ fontWeight: 600 }}>Resolved subtrack:</span>{' '}
        {subtrack.subtrack_id}
        {subtrack.mnemonic ? ` · ${subtrack.mnemonic}` : ''}
        {subtrack.track ? ` · ${String(subtrack.track).toUpperCase()}` : ''}
        {subtrack.status ? ` · ${subtrack.status}` : ''}
      </div>
      {isOverride && (
        <span style={{
          background: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)',
          padding: '2px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 500,
        }}>
          Overriding → {selectedRow?.preset_id}
        </span>
      )}
    </div>
  );
}

/* ─── Left-column subtrack option (radio) ─── */
function SubtrackOption({ preset, checked, suggested, onSelect, extra }) {
  return (
    <div style={{
      background: checked ? 'var(--wi-background-info)' : 'var(--wi-background-secondary)',
      border: checked ? '1.5px solid var(--wi-action-primary)' : '1px solid var(--wi-divider)',
      borderRadius: 10,
      padding: checked ? '11px 13px' : '11.5px 13.5px',
      transition: 'border-color 150ms ease, background 150ms ease',
    }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input
          type="radio"
          name="subtrack-pathway"
          checked={checked}
          onChange={onSelect}
          style={{ marginTop: 3, accentColor: 'var(--wi-action-primary)', cursor: 'pointer' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--wi-text-secondary)' }}>{preset.preset_id}</span>
            {suggested && (
              <span style={{
                background: 'var(--wi-status-info-10)', color: 'var(--wi-status-info)',
                padding: '1px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 600,
              }}>Suggested</span>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--wi-text-primary)', lineHeight: 1.3, marginTop: 1 }}>
            {preset.label}
          </div>
          {preset.guideline && (
            <div style={{ fontSize: 11.5, color: 'var(--wi-text-secondary)', marginTop: 2, lineHeight: 1.4 }}>
              {preset.guideline}
            </div>
          )}
        </div>
      </label>
      {extra && <div style={{ marginTop: 10, marginLeft: 24 }}>{extra}</div>}
    </div>
  );
}

/* ─── CKD stage segmented control ─── */
function CkdStageSegmented({ stage, onChange, options = ['3a', '3b'] }) {
  return (
    <div style={{
      display: 'inline-flex', gap: 4,
      padding: 3, borderRadius: 8,
      background: 'var(--wi-background-primary)',
    }}>
      {options.map(s => {
        const active = stage === s;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            style={{
              padding: '5px 14px',
              fontSize: 12, fontWeight: 500,
              borderRadius: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'var(--wi-font-family)',
              background: active ? 'var(--wi-action-primary)' : 'transparent',
              color: active ? 'var(--wi-on-action-primary)' : 'var(--wi-text-secondary)',
              transition: 'background 150ms ease',
            }}
          >
            Stage {s}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Diagnosis panel — the recorded-diagnosis change Apply attests ───
   One chip per condition, the billed ICD-10 inline (same math as $align);
   "chart only" = attested + written but never billed (subtrack-only rule). */
function DiagnosisPanel({ delta, rows = [] }) {
  const changed = hasDiagnosisChange(delta);
  return (
    <div style={{
      background: 'var(--wi-background-secondary)',
      border: changed ? '1.5px solid var(--wi-status-moderate)' : '1px solid var(--wi-divider)',
      borderRadius: 10, padding: 16, marginBottom: 12,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: 'var(--wi-text-secondary)',
      }}>
        Diagnosis — ICD-10 submitted to CMS
      </div>
      <ChipRow>
        {rows.map(r => (
          <Chip
            key={`${r.state}-${r.label}`}
            tone={r.state === 'added' ? 'good' : r.state === 'removed' ? 'bad' : r.chartOnly ? 'neutral' : 'info'}
          >
            <span title={r.display || undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span>{r.state === 'added' ? '+ ' : r.state === 'removed' ? '− ' : ''}{r.label}</span>
              {r.code && <span style={{ fontWeight: 700 }}>· {r.code}</span>}
              {r.chartOnly && <span style={{ opacity: 0.75 }}>· chart only</span>}
              {r.unsupported && <span style={{ opacity: 0.75 }}>(unsupported value)</span>}
            </span>
          </Chip>
        ))}
      </ChipRow>
      <div style={{
        fontSize: 12, lineHeight: 1.5,
        color: changed ? 'var(--wi-text-primary-moderate)' : 'var(--wi-text-secondary)',
      }}>
        {changed
          ? 'Applying this pathway updates the recorded diagnosis as shown — removed conditions (and their codes) leave the care plan and future CMS submissions. "Chart only" conditions stay on the chart but are never billed to CMS.'
          : 'Diagnosis unchanged — this pathway matches the recorded conditions. The codes shown are what CMS receives at alignment.'}
      </div>
    </div>
  );
}

/* ─── Empty preview state ─── */
function EmptyPreview() {
  return (
    <div style={{
      background: 'var(--wi-background-secondary)',
      border: '1px dashed var(--wi-divider)',
      borderRadius: 10,
      padding: '48px 20px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: 28, lineHeight: 1,
        background: 'linear-gradient(135deg, #FF6B9D 0%, #C77DFF 50%, #1F4AC2 100%)',
        WebkitBackgroundClip: 'text', backgroundClip: 'text',
        WebkitTextFillColor: 'transparent', color: 'transparent',
      }}>✦</div>
      <div style={{ fontSize: 13, color: 'var(--wi-text-secondary)', maxWidth: 240, lineHeight: 1.5 }}>
        Select a subtrack pathway to preview the resulting care plan.
      </div>
    </div>
  );
}

/* ─── Right-column preview body ─── */
function PreviewPanel({ preview }) {
  const c = preview.changes || {};
  const conflicts = preview.conflicts || [];
  const applied = preview.applied_pathways || [];
  const targets = c.targets || {};
  const monitoring = c.monitoring_cadence || {};
  const risk = (c.risk_level || '').toLowerCase();
  const riskTone =
    risk === 'critical' || risk === 'crisis' ? 'bad' :
    risk === 'high' || risk === 'elevated' ? 'moderate' :
    risk ? 'good' : 'neutral';

  return (
    <div style={{
      background: 'var(--wi-background-secondary)',
      border: '1px solid var(--wi-divider)',
      borderRadius: 10,
      padding: 16,
      display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      {conflicts.length > 0 && (
        <div style={{
          background: 'var(--wi-background-moderate)',
          color: 'var(--wi-text-primary-moderate)',
          padding: '10px 12px', borderRadius: 8,
          display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12.5,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} for clinical review
            </div>
            <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.5 }}>
              {conflicts.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        </div>
      )}

      {applied.length > 0 && (
        <Section label={`Applied pathways (${applied.length})`}>
          <ChipRow>
            {applied.map(p => <Chip key={p} tone="info">{p}</Chip>)}
          </ChipRow>
        </Section>
      )}

      <Section label="Track · Risk">
        <ChipRow>
          {c.track && <Chip tone="info">{c.track}</Chip>}
          {c.risk_level && <Chip tone={riskTone}>risk · {c.risk_level}</Chip>}
          {!c.track && !c.risk_level && <Muted>—</Muted>}
        </ChipRow>
      </Section>

      {Object.keys(targets).length > 0 && (
        <Section label="Targets">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(targets).map(([m, t]) => (
              <TargetRow
                key={m}
                name={m}
                display={t.display || `${t.operator || ''} ${t.value}`.trim()}
                source={t._source}
              />
            ))}
          </div>
        </Section>
      )}

      {Object.keys(monitoring).length > 0 && (
        <Section label="Monitoring cadence">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(monitoring).map(([m, cd]) => (
              <MonitorRow
                key={m}
                name={m}
                cadence={cd.times_per_week ? `${cd.times_per_week}×/wk` : null}
                timeOfDay={cd.time_of_day}
                raw={cd}
              />
            ))}
          </div>
        </Section>
      )}

      <Section label="Devices">
        <ChipRow>
          {(c.devices || []).length === 0
            ? <Muted>—</Muted>
            : c.devices.map(d => <Chip key={d} tone="neutral">{d}</Chip>)}
        </ChipRow>
      </Section>

      <Section label="Content curriculum">
        <ChipRow>
          {(c.content_curriculum || []).length === 0
            ? <Muted>—</Muted>
            : c.content_curriculum.map(t => <Chip key={t} tone="info-soft">{t}</Chip>)}
        </ChipRow>
      </Section>

      <Section label="Medication classes to consider">
        <ChipRow>
          {(c.medication_classes || []).length === 0
            ? <Muted>—</Muted>
            : c.medication_classes.map(m => <Chip key={m} tone="outline">{m}</Chip>)}
        </ChipRow>
      </Section>

      <Section label="Lifestyle focus">
        <ChipRow>
          {(c.lifestyle_focus || []).length === 0
            ? <Muted>—</Muted>
            : c.lifestyle_focus.map(l => <Chip key={l} tone="good">{l}</Chip>)}
        </ChipRow>
      </Section>
    </div>
  );
}

/* ─── Preview helpers ─── */

function Section({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: 'var(--wi-text-secondary)',
        marginBottom: 6,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ChipRow({ children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
  );
}

function Chip({ children, tone = 'neutral' }) {
  const styles = {
    neutral:    { background: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)', border: '1px solid var(--wi-divider)' },
    info:       { background: 'var(--wi-background-info)',    color: 'var(--wi-text-primary-info)' },
    'info-soft':{ background: 'var(--wi-status-info-10)',     color: 'var(--wi-status-info)' },
    good:       { background: 'var(--wi-background-good)',    color: 'var(--wi-text-primary-good)' },
    moderate:   { background: 'var(--wi-background-moderate)',color: 'var(--wi-text-primary-moderate)' },
    bad:        { background: 'var(--wi-background-bad)',     color: 'var(--wi-text-primary-bad)' },
    outline:    { background: 'var(--wi-background-secondary)', color: 'var(--wi-text-secondary)', border: '1px solid var(--wi-divider)' },
  }[tone] || {};
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 500, lineHeight: 1.4,
      ...styles,
    }}>{children}</span>
  );
}

function Muted({ children }) {
  return <span style={{ color: 'var(--wi-text-placeholder)', fontSize: 13 }}>{children}</span>;
}

function TargetRow({ name, display, source }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px',
      background: 'var(--wi-background-primary)',
      borderRadius: 8,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: 'var(--wi-text-primary)',
        minWidth: 72, flexShrink: 0,
      }}>
        {name}
      </div>
      <div style={{ flex: 1, fontSize: 13, color: 'var(--wi-text-primary)', minWidth: 0 }}>
        {display}
      </div>
      {source && (
        <span style={{
          background: 'var(--wi-status-info-10)',
          color: 'var(--wi-status-info)',
          padding: '2px 8px', borderRadius: 999,
          fontSize: 10.5, fontWeight: 500, flexShrink: 0,
        }}>{source}</span>
      )}
    </div>
  );
}

function MonitorRow({ name, cadence, timeOfDay, raw }) {
  const hasTime = Array.isArray(timeOfDay) && timeOfDay.length > 0;
  const fallback = !cadence && !hasTime ? JSON.stringify(raw) : null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px',
      background: 'var(--wi-background-primary)',
      borderRadius: 8,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: 'var(--wi-text-primary)',
        minWidth: 72, flexShrink: 0,
      }}>
        {name}
      </div>
      <div style={{ flex: 1, fontSize: 13, color: 'var(--wi-text-primary)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {cadence && <span>{cadence}</span>}
        {hasTime && (
          <span style={{ color: 'var(--wi-text-secondary)' }}>· {timeOfDay.join(' / ')}</span>
        )}
        {fallback && <span style={{ color: 'var(--wi-text-secondary)' }}>{fallback}</span>}
      </div>
    </div>
  );
}
